import * as React from 'react';
import cn from 'classnames';
import {Link} from 'react-router-dom';
import {styled} from '@mui/material/styles';
import {MuiSxProps} from '@practicegenius/pg-ui/src/base/Material/utils/sx-helpers';
import {Spinner} from './Spinner';
import {
  socialColors,
  socialBorders,
  socialTextColors,
  socialButtonIcons,
  socialColorsHover,
} from '../constants/colors';

export const BTN_STYLES = {
  facebook: 'facebook',
  google: 'google',
  apple: 'apple',
  email: 'email',
};
const hasSocialIcon = (btnstyle?: string) =>
  btnstyle && Object.keys(BTN_STYLES).includes(btnstyle);

type Props = React.ComponentProps<'button'> & {
  children: React.ReactNode;
  className?: string;
  btnStyle?: LegacyButtonStyle;
  disabled?: boolean;
  to?: string | Location;
  href?: string;
  loading?: boolean;
  width?: string;
  height?: string;
  sx?: MuiSxProps;
};

export type LegacyButtonStyle = keyof typeof BTN_STYLES;

const socialButtonHeight = 55;
const normalButtonHeight = 46;
const buttonHeight = (isSocial: boolean) =>
  isSocial ? socialButtonHeight : normalButtonHeight;

const ButtonContainer = styled('button')<{
  btnstyle?: LegacyButtonStyle;
  to?: Props['to'];
  href?: Props['href'];
}>(({theme, btnstyle, to, href}) => {
  const hasBtnStyle = Boolean(btnstyle);
  return {
    padding: 0,
    position: 'relative',
    height: `${buttonHeight(hasBtnStyle)}px`,
    display: to || href || btnstyle ? 'flex' : undefined,
    flexDirection: (() => {
      switch (true) {
        case hasSocialIcon(btnstyle):
          return 'row';
        case Boolean(to || href):
          return 'column';
        default:
          return undefined;
      }
    })(),
    justifyContent: (() => {
      switch (true) {
        case hasSocialIcon(btnstyle):
          return 'flex-start';
        case Boolean(to || href):
          return 'center';
        default:
          return 'center';
      }
    })(),
    border: btnstyle && socialBorders[btnstyle](theme),
    color: btnstyle && socialTextColors[btnstyle](theme),
    width: btnstyle && '300px',
    backgroundColor: btnstyle && socialColors[btnstyle](theme),
    alignItems: btnstyle && 'center',

    '&&&:hover': {
      backgroundColor: btnstyle
        ? socialColorsHover[btnstyle](theme)
        : undefined,
    },
  };
});

const IconWrapper = styled('div')<{
  btnstyle: LegacyButtonStyle & undefined;
}>(({btnstyle}) => ({
  BorderTopLeftRadius: '5px',
  borderBottomLeftRadius: '5px',
  backgroundColor: btnstyle ? 'transparent' : 'rgba(0, 0, 0, 0.15)',
  width: `${socialButtonHeight + 20}px`,
  height: `${socialButtonHeight}px`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const ChildrenWrapper = styled('span')<{
  hasIcon: boolean;
  $loading: boolean;
}>(({hasIcon, $loading}) => ({
  position: hasIcon ? 'relative' : undefined,
  display: 'flex',
  alignItems: 'center',
  padding: '5px 15px',
  width: '100%',

  '.children': {
    opacity: 1,
    transition: 'all 0.5s ease',
    width: 'fit-content',
    textTransform: hasIcon ? 'none' : undefined,
    left: (() => {
      switch (true) {
        case $loading && !hasIcon:
          return '15px';
        case !$loading && !hasIcon:
          return '50%';
        default:
      }
    })(),
    position: !hasIcon ? 'absolute' : undefined,
    transform: !hasIcon && !$loading ? 'translateX(-50%)' : undefined,
  },

  '.spin-wrapper': {
    transition: 'all 0.5s ease-out',
    opacity: $loading ? 1 : 0,
    position: 'absolute',
    width: 'auto',
    right: '15px',
  },
}));

// TODO refactor all usages of this to use pg-ui Button
export function Button({
  btnStyle,
  disabled,
  className = '',
  children,
  loading,
  type = 'button',
  ...buttonProps
}: Props) {
  const ButtonIcon = btnStyle && socialButtonIcons[btnStyle];

  return (
    <ButtonContainer
      as={buttonProps.to ? Link : (buttonProps.href && 'a') || 'button'}
      type={type}
      {...buttonProps}
      // @ts-ignore
      btnstyle={btnStyle}
      className={cn(`button ${className}`, {disabled})}
      disabled={loading || disabled}
    >
      <>
        {ButtonIcon && (
          // @ts-ignore
          <IconWrapper btnstyle={btnStyle}>
            <ButtonIcon />
          </IconWrapper>
        )}
        <ChildrenWrapper
          $loading={Boolean(loading)}
          hasIcon={Boolean(ButtonIcon)}
        >
          <span className="children">{children}</span>
          <span className="spin-wrapper">
            <Spinner size={20} />
          </span>
        </ChildrenWrapper>
      </>
    </ButtonContainer>
  );
}
